/*eslint max-lines-per-function: ["error", 170]*/
import React from 'react';
import { Button, Card, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import {
  ArrowDownShort,
  ArrowUpShort,
  PencilSquare,
  Trash,
} from 'react-bootstrap-icons';

import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useTranslation } from '../../hooks/useTranslation';

export interface SortableListColumn<T> {
  header: React.ReactNode;
  render: (item: T, index: number) => React.ReactNode;
  width?: string | number;
}

interface SortableListProps<T> {
  items: T[];
  columns: SortableListColumn<T>[];
  onAdd?: () => void;
  addLabel?: React.ReactNode;
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  renderRowExtra?: (item: T, index: number) => React.ReactNode;
  readOnly?: boolean;
  emptyMessage?: string;
  deleteConfirmation?: string;
  editTooltip?: string;
  deleteTooltip?: string;
  moveUpTooltip?: string;
  moveDownTooltip?: string;
  bordered?: boolean;
  title?: React.ReactNode;
}

export const SortableList = <T,>({
  items,
  columns,
  onAdd,
  addLabel,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  renderRowExtra,
  readOnly,
  emptyMessage,
  deleteConfirmation,
  editTooltip,
  deleteTooltip,
  moveUpTooltip,
  moveDownTooltip,
  bordered,
  title,
}: SortableListProps<T>) => {
  const { t, tk } = useTranslation('projectLng');
  const resolvedDeleteConfirmation =
    deleteConfirmation ?? t(tk.button.deleteConfirm);
  const resolvedEditTooltip = editTooltip ?? t(tk.button.edit);
  const resolvedDeleteTooltip = deleteTooltip ?? t(tk.button.delete);
  const resolvedMoveUpTooltip = moveUpTooltip ?? t(tk.button.moveUp);
  const resolvedMoveDownTooltip = moveDownTooltip ?? t(tk.button.moveDown);
  const sortable = !readOnly && (!!onMoveUp || !!onMoveDown);
  const hasActions = !readOnly && (!!onEdit || !!onDelete);

  return (
    <div>
      {!readOnly && onAdd && (
        <div className="mb-4">
          <Button variant="outline-primary" onClick={onAdd}>
            {addLabel}
          </Button>
        </div>
      )}
      {title && <Card.Title className="mb-3">{title}</Card.Title>}
      {items.length === 0 && emptyMessage ? (
        <div className="text-center text-muted p-4">{emptyMessage}</div>
      ) : (
        <Table striped bordered={bordered}>
          <thead>
            <tr>
              {sortable && <th style={{ width: 60 }} />}
              {columns.map((col, i) => (
                <th
                  key={i}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
              {hasActions && <th />}
              {renderRowExtra && <th />}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                onClick={() => !readOnly && onEdit?.(index)}
                className={!readOnly && onEdit ? 'grid-clickable-row' : ''}
              >
                {sortable && (
                  <td onClick={e => e.stopPropagation()}>
                    <div className="d-flex gap-1">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{resolvedMoveUpTooltip}</Tooltip>}
                      >
                        <ArrowUpShort
                          size={24}
                          className={
                            index === 0
                              ? 'text-muted grid-reorder-arrow-disabled'
                              : 'text-warning grid-reorder-arrow'
                          }
                          onClick={e => {
                            if (index === 0) {
                              return;
                            }
                            e.stopPropagation();
                            onMoveUp?.(index);
                          }}
                        />
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{resolvedMoveDownTooltip}</Tooltip>}
                      >
                        <ArrowDownShort
                          size={24}
                          className={
                            index === items.length - 1
                              ? 'text-muted grid-reorder-arrow-disabled'
                              : 'text-info grid-reorder-arrow'
                          }
                          onClick={e => {
                            if (index === items.length - 1) {
                              return;
                            }
                            e.stopPropagation();
                            onMoveDown?.(index);
                          }}
                        />
                      </OverlayTrigger>
                    </div>
                  </td>
                )}
                {columns.map((col, i) => (
                  <td key={i}>{col.render(item, index)}</td>
                ))}
                {hasActions && (
                  <td onClick={e => e.stopPropagation()}>
                    <div className="d-flex gap-2">
                      {onEdit && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{resolvedEditTooltip}</Tooltip>}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={e => {
                              e.stopPropagation();
                              onEdit(index);
                            }}
                          >
                            <PencilSquare />
                          </Button>
                        </OverlayTrigger>
                      )}
                      {onDelete && (
                        <ConfirmationButton
                          variant="outline-danger"
                          className="btn-sm"
                          tooltip={resolvedDeleteTooltip}
                          confirmation={resolvedDeleteConfirmation}
                          onOK={() => onDelete(index)}
                        >
                          <Trash />
                        </ConfirmationButton>
                      )}
                    </div>
                  </td>
                )}
                {renderRowExtra && (
                  <td onClick={e => e.stopPropagation()}>
                    {renderRowExtra(item, index)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};
